import React from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';

function InternetEditorDialog({ context, primitives }) {
    const {
        addInternetDisplayChip,
        addInternetTopBadge,
        deleteDraftOnlyProduct,
        editingInternet,
        findActiveInternetProduct,
        isDraftOnlyProduct,
        isValidSortOrderInput,
        removeInternetDisplayChip,
        removeInternetTopBadge,
        saveInternetDraft,
        savingInternetDraft,
        setEditingInternet,
        updateInternetDisplayChip,
        updateInternetEditorValue,
        updateInternetTopBadge,
        updateInternetTvPriceAdjustmentValue,
        validateInternetEditor
    } = context;
    const {
        renderChipListEditor,
        renderOfferLifecycleControls,
        renderProductDraftPreview
    } = primitives;

    const renderInternetEditDialog = () => {
        if (!editingInternet) {
            return null;
        }

        const values = editingInternet.values;
        const localErrors = validateInternetEditor(values);
        const sortOrderInvalid = !isValidSortOrderInput(values.sortOrder);
        const activeProduct = findActiveInternetProduct(editingInternet.originalId);
        const canDelete = isDraftOnlyProduct({ id: editingInternet.originalId, ...values }, activeProduct);

        return (
            <Dialog open onClose={() => setEditingInternet(null)} fullWidth maxWidth="md">
                <DialogTitle>Internet Draft Edit</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="warning" variant="outlined">
                            Saving updates the draft config only. Publish Internet Draft is required before Bundle Builder sees these values.
                        </Alert>
                        <Alert severity="info" variant="outlined">
                            Most updates only need Offer Settings, Visibility, and Expiry Date. Customer email output changes live in the Customer Email Settings section.
                        </Alert>
                        {localErrors.length > 0 && (
                            <Alert severity="error" variant="outlined">
                                {localErrors.slice(0, 3).join(' ')}
                                {localErrors.length > 3 ? ` ${localErrors.length - 3} more issue(s).` : ''}
                            </Alert>
                        )}
                        <Typography variant="h6">Offer Settings / Bundle Builder Display</Typography>
                        <TextField
                            label="Product key"
                            value={editingInternet.originalId}
                            fullWidth
                            disabled
                            helperText="Product keys cannot change in this phase."
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    label="Display name"
                                    value={values.displayName}
                                    onChange={(event) => updateInternetEditorValue('displayName', event.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Sort order"
                                    type="number"
                                    value={values.sortOrder}
                                    onChange={(event) => updateInternetEditorValue('sortOrder', event.target.value)}
                                    fullWidth
                                    error={sortOrderInvalid}
                                    helperText={sortOrderInvalid ? 'Sort order must be numeric.' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="PAB/base price"
                                    type="number"
                                    value={values.belowLinePrice}
                                    onChange={(event) => updateInternetEditorValue('belowLinePrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Regular/reference price"
                                    type="number"
                                    value={values.aboveLinePrice}
                                    onChange={(event) => updateInternetEditorValue('aboveLinePrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Mobility discount price"
                                    type="number"
                                    value={values.mobilityBelowLinePrice}
                                    onChange={(event) => updateInternetEditorValue('mobilityBelowLinePrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Mobility reference price"
                                    type="number"
                                    value={values.mobilityAboveLinePrice}
                                    onChange={(event) => updateInternetEditorValue('mobilityAboveLinePrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="EPP with mobility price"
                                    type="number"
                                    value={values.eppMobilityBelowLinePrice}
                                    onChange={(event) => updateInternetEditorValue('eppMobilityBelowLinePrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="EPP with mobility reference price"
                                    type="number"
                                    value={values.eppMobilityAboveLinePrice}
                                    onChange={(event) => updateInternetEditorValue('eppMobilityAboveLinePrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="PAB adjustment amount"
                                    type="number"
                                    value={values.pabAdjustmentAmount}
                                    onChange={(event) => updateInternetEditorValue('pabAdjustmentAmount', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                    helperText="Amount added when PAB is off. Fibre is normally $10; Copper/Wireless is $5."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Customer email regular price"
                                    type="number"
                                    value={values.emailRegularPrice}
                                    onChange={(event) => updateInternetEditorValue('emailRegularPrice', event.target.value)}
                                    fullWidth
                                    inputProps={{ min: 0, step: '0.01' }}
                                    helperText="Optional regular column for customer email savings. Leave blank to use promo price."
                                />
                            </Grid>
                        </Grid>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <FormControlLabel
                                control={<Switch checked={values.mobilityDiscountEligible} onChange={(event) => updateInternetEditorValue('mobilityDiscountEligible', event.target.checked)} />}
                                label="M&H discount eligible"
                            />
                            <FormControlLabel
                                control={<Switch checked={values.eppEligible} onChange={(event) => updateInternetEditorValue('eppEligible', event.target.checked)} />}
                                label="EPP discount eligible"
                            />
                        </Stack>
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Temporary TV Price Adjustment</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Temporary. Applies only in Bundle Builder when this Internet plan and TV are selected. Does not change TV V2 pricing.
                            </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={Boolean(values.tvPriceAdjustment?.enabled)}
                                        onChange={(event) => updateInternetTvPriceAdjustmentValue('enabled', event.target.checked)}
                                    />
                                )}
                                label="Increase TV price when this Internet plan is selected"
                            />
                            <TextField
                                label="Adjustment amount"
                                type="number"
                                value={values.tvPriceAdjustment?.amount ?? 0}
                                onChange={(event) => updateInternetTvPriceAdjustmentValue('amount', event.target.value)}
                                disabled={!values.tvPriceAdjustment?.enabled}
                                inputProps={{ min: 0, step: '0.01' }}
                                helperText="Monthly TV increase."
                                sx={{ minWidth: { sm: 220 } }}
                            />
                        </Stack>
                        <TextField
                            label="Adjustment label"
                            value={values.tvPriceAdjustment?.label || ''}
                            onChange={(event) => updateInternetTvPriceAdjustmentValue('label', event.target.value)}
                            disabled={!values.tvPriceAdjustment?.enabled}
                            fullWidth
                            helperText="Shown to agents in Bundle Builder quote details."
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <FormControlLabel
                                control={<Switch checked={values.recommended} onChange={(event) => updateInternetEditorValue('recommended', event.target.checked)} />}
                                label="Recommended"
                            />
                            <FormControlLabel
                                control={<Switch checked={values.hot} onChange={(event) => updateInternetEditorValue('hot', event.target.checked)} />}
                                label="Hot"
                            />
                        </Stack>
                        <TextField
                            label="Highlight note"
                            value={values.highlightNote}
                            onChange={(event) => updateInternetEditorValue('highlightNote', event.target.value)}
                            fullWidth
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <FormControlLabel
                                control={<Switch checked={values.visible} onChange={(event) => updateInternetEditorValue('visible', event.target.checked)} />}
                                label="Visible"
                            />
                            <FormControlLabel
                                control={<Switch checked={values.quoteEligible} onChange={(event) => updateInternetEditorValue('quoteEligible', event.target.checked)} />}
                                label="Quote eligible"
                            />
                        </Stack>
                        {renderOfferLifecycleControls({
                            values,
                            updateValue: updateInternetEditorValue,
                            canDelete,
                            onDelete: () => deleteDraftOnlyProduct({ scope: 'internet', productId: editingInternet.originalId }),
                            helperScope: 'Internet'
                        })}
                        <Divider />
                        <Box component="details">
                            <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 700 }}>
                                Advanced Settings
                            </Box>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                <TextField
                                    label="Internal agent note"
                                    value={values.internalNotesText}
                                    onChange={(event) => updateInternetEditorValue('internalNotesText', event.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    helperText="Use one note per line."
                                />
                            </Stack>
                        </Box>
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Offer Display Details</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Draft display settings affect Bundle Builder only after Internet publish.
                            </Typography>
                        </Box>
                        <TextField
                            label="Card subtitle / description"
                            value={values.cardDescription}
                            onChange={(event) => updateInternetEditorValue('cardDescription', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Display highlight note"
                            value={values.displayHighlightNote}
                            onChange={(event) => updateInternetEditorValue('displayHighlightNote', event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Internal agent display note"
                            value={values.internalAgentNote}
                            onChange={(event) => updateInternetEditorValue('internalAgentNote', event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Selected badge text"
                            value={values.selectedBadgeText}
                            onChange={(event) => updateInternetEditorValue('selectedBadgeText', event.target.value)}
                            fullWidth
                        />
                        {renderChipListEditor({
                            title: 'Top Card Badges',
                            helperText: 'Top Card Badges appear at the top of the Bundle Builder card.',
                            chips: values.topBadges,
                            onUpdate: updateInternetTopBadge,
                            onAdd: addInternetTopBadge,
                            onRemove: removeInternetTopBadge,
                            addLabel: 'Add Badge'
                        })}
                        {renderChipListEditor({
                            title: 'Detail Chips',
                            helperText: 'Detail Chips appear inside the card under the product description.',
                            chips: values.displayChips,
                            onUpdate: updateInternetDisplayChip,
                            onAdd: addInternetDisplayChip,
                            onRemove: removeInternetDisplayChip,
                            addLabel: 'Add Chip'
                        })}
                        <Divider />
                        <Box>
                            <Typography variant="h6" gutterBottom>Customer Email Settings</Typography>
                            <Typography variant="body2" color="text.secondary">
                                These fields control customer-facing email output only.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={values.customerEmailEligible} onChange={(event) => updateInternetEditorValue('customerEmailEligible', event.target.checked)} />}
                            label="Customer email eligible"
                        />
                        <TextField
                            label="Customer-facing email name"
                            value={values.emailDisplayName}
                            onChange={(event) => updateInternetEditorValue('emailDisplayName', event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Customer-facing email bullets"
                            value={values.emailBulletsText}
                            onChange={(event) => updateInternetEditorValue('emailBulletsText', event.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="Use one bullet per line. Required when customer email eligible is on."
                        />
                        {renderProductDraftPreview(values, {
                            categoryLabel: 'Internet',
                            priceValue: values.belowLinePrice
                        })}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingInternet(null)} disabled={savingInternetDraft}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={saveInternetDraft} disabled={savingInternetDraft || localErrors.length > 0 || sortOrderInvalid}>
                        {savingInternetDraft ? 'Saving...' : 'Save Draft'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    return renderInternetEditDialog();
}

export default InternetEditorDialog;
